// src/components/settings/SettingField.jsx
//
// One setting: label, explanation, where the value comes from, and the
// control. Controls are chosen by type from the server registry.

import React from 'react';
import RoleMatrix from './RoleMatrix';
import { formatValue, formatWhen } from './settingsShared';

function SourceLine({ s, resetting, onReset, onUndoReset }) {
  if (resetting) {
    return (
      <span className="st-source is-pending">
        Will go back to {s.env_name ? `.env or default` : 'default'} ·{' '}
        <button type="button" className="st-linkbtn" onClick={onUndoReset}>Undo</button>
      </span>
    );
  }
  if (s.source === 'settings') {
    return (
      <span className="st-source is-set">
        Set by {s.updated_by}{s.updated_at ? `, ${formatWhen(s.updated_at)}` : ''} ·{' '}
        <button type="button" className="st-linkbtn" onClick={onReset}>Reset</button>
      </span>
    );
  }
  if (s.source === 'env') {
    return <span className="st-source">From .env ({s.env_name}). Saving here takes over.</span>;
  }
  return <span className="st-source">Default</span>;
}

export default function SettingField({ s, draft, error, dirty, resetting, onChange, onReset, onUndoReset }) {
  const id = `st-${s.key.replace(/\W/g, '-')}`;
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  const describedBy = [helpId, error ? errId : null].filter(Boolean).join(' ');
  const disabled = resetting;

  let control;
  if (s.type === 'role_matrix') {
    control = <RoleMatrix setting={s} value={draft || {}} saved={s.value || {}} onChange={onChange} disabled={disabled} />;
  } else if (s.type === 'multi_choice') {
    const picked = Array.isArray(draft) ? draft : [];
    control = (
      <div className="st-choices" role="group" aria-labelledby={`${id}-label`} aria-describedby={describedBy}>
        {s.options.map((o) => {
          const on = picked.includes(o.id);
          const locked = (s.required_items || []).includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              className={`st-choice ${on ? 'is-on' : ''} ${locked ? 'is-locked' : ''}`}
              aria-pressed={on}
              disabled={disabled || locked}
              title={locked ? 'Always on' : undefined}
              onClick={() => onChange(on ? picked.filter((x) => x !== o.id) : [...picked, o.id])}
            >
              <i className={`fas ${on ? 'fa-check' : 'fa-plus'}`} aria-hidden="true"></i>
              {o.label}
              {locked && <i className="fas fa-lock st-choice-lock" aria-hidden="true"></i>}
            </button>
          );
        })}
      </div>
    );
  } else if (s.type === 'email_list') {
    control = (
      <textarea
        id={id}
        className="st-input st-textarea"
        rows={Math.min(6, Math.max(3, String(draft || '').split('\n').length + 1))}
        value={draft}
        disabled={disabled}
        placeholder="Empty: every active admin"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        inputMode="email"
        aria-describedby={describedBy}
        aria-invalid={!!error}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else if (s.type === 'int' || s.type === 'float') {
    control = (
      <div className="st-number">
        <input
          id={id}
          className="st-input"
          type="text"
          inputMode={s.type === 'float' ? 'decimal' : 'numeric'}
          value={draft}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={!!error}
          onChange={(e) => onChange(e.target.value.replace(s.type === 'float' ? /[^0-9.]/g : /[^0-9]/g, ''))}
        />
        {s.unit && <span className="st-unit" aria-hidden="true">{s.unit}</span>}
        <span className="st-range">{s.min}–{s.max}</span>
      </div>
    );
  } else {
    control = (
      <input
        id={id}
        className="st-input"
        type="text"
        maxLength={s.max_length}
        value={draft}
        disabled={disabled}
        autoCapitalize="characters"
        aria-describedby={describedBy}
        aria-invalid={!!error}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <div className={`st-field ${s.type === 'role_matrix' ? 'is-wide' : ''} ${dirty ? 'is-dirty' : ''} ${error ? 'has-error' : ''}`}>
      <div className="st-field-text">
        <label id={`${id}-label`} htmlFor={s.type === 'multi_choice' ? undefined : id} className="st-field-label">
          {s.label}
          {dirty && <span className="st-dirty-dot" aria-label="Changed, not saved" />}
        </label>
        <p id={helpId} className="st-help">{s.help}</p>
        <SourceLine s={s} resetting={resetting} onReset={onReset} onUndoReset={onUndoReset} />
      </div>
      <div className="st-field-control">
        {control}
        {resetting && s.type !== 'role_matrix' && <div className="st-reset-preview">Saved value {formatValue(s, s.value)} will be removed.</div>}
        {error && <div id={errId} className="st-error" role="alert">{error}</div>}
      </div>
    </div>
  );
}
